/* istanbul ignore file */
import React, { useEffect } from 'react';
import {
  AuthorizationNotifier,
  AuthorizationServiceConfiguration,
  BaseTokenRequestHandler,
  DefaultCrypto,
  FetchRequestor,
  GRANT_TYPE_AUTHORIZATION_CODE,
  LocalStorageBackend,
  RedirectRequestHandler,
  StringMap,
  TokenRequest,
  TokenResponse,
} from '@openid/appauth';
import environment from 'environment';
import { NoHashQueryStringUtils } from 'auth/noHashQueryStringUtils';
import { decodeJwt } from 'auth/jwt';
import { FpLoading } from 'components/atoms/FpLoading';
import { LoginUser } from 'types/model/user';
import { useUserReadFacade } from 'redux/user-read/user-read.facade';
import { useProjectReadFacade } from 'redux/project-read/project-read.facade';
import { useDefaultProjectUpdateFacade } from 'redux/default-project-update/default-project-update.facade';
import { getLocalStorageReturnUrl, removeLocalStorageReturnUrl } from 'utils/FpLocalStorage/local-storage';
import { appInsights } from 'azure-application-insights';
import { getUserProperties } from 'hooks/application-insights-user-hook';
import { useNavigate } from 'react-router-dom';
// import { LOCALE_TYPE } from 'locales/locale';
// import { initialUserValue } from 'redux/initial-state';
// import { LocaleType } from 'types/constants/locale';

const POSITION_TYPE_PARAM_KEY = 'position_type';
const PROJECT_ID_PARAM_KEY = 'project_id';

export function FpCallback() {
  const {
    setAccessToken, setUser, setIdToken, setRefreshToken, setRefreshInterval,
  } = useUserReadFacade();
  const {
    loadDefaultProject, loadProject, loaded, setPositionType,
  } = useProjectReadFacade();
  const { updateDefaultProject } = useDefaultProjectUpdateFacade();
  const navigate = useNavigate();

  /**
   * APIのロード状態を監視してリダイレクトさせる
   */
  useEffect(() => {
    const wait = async (available: boolean): Promise<void> => new Promise((resolve) => {
      if (available) {
        resolve();
      }
    });

    wait(loaded).then(() => {
      const returnUrl = getLocalStorageReturnUrl() || '/';
      removeLocalStorageReturnUrl();
      navigate(returnUrl);
    });
  }, [loaded, navigate]);

  /**
   * 認証情報を保存する.<br>
   * 最初の1回しか呼ばれないように監視する変数は注意して追加してください
  */
  useEffect(() => {
    const initializeUser = (oResponse: TokenResponse): LoginUser => {
      // ユーザー情報初期化
      setIdToken(oResponse.idToken || '');
      setAccessToken(oResponse.accessToken);
      setRefreshToken(oResponse.refreshToken || '');

      const user: LoginUser = decodeJwt(oResponse.accessToken);
      setUser(user);

      const internal = (user.exp * 1000) - new Date().getTime();
      const offset = 0.8;
      setRefreshInterval(internal * offset);

      return user;
    };

    // アプリ共通で初期化したい処理
    const initializeApp = async () => {
      const domain = window.location.origin;
      const returnUrl = new URL(`${domain}/${getLocalStorageReturnUrl() || ''}`);
      const searchParams = new URLSearchParams(returnUrl.search);
      const positionType: string | null = searchParams.get(POSITION_TYPE_PARAM_KEY);
      setPositionType(positionType);
      const projectId: string | null = searchParams.get(PROJECT_ID_PARAM_KEY);
      // プロジェクト指定の場合
      if (projectId) {
        await loadProject(projectId);
        await updateDefaultProject(projectId);
        return;
      }
      await loadDefaultProject();
    };

    // 開発環境の時だけ手動で初期化する
    // if (process.env.NODE_ENV === 'development') {
    //   setAccessToken('test');
    //   setUser({
    //     ...initialUserValue,
    //     given_name: '太郎',
    //     family_name: 'テスト',
    //     locale: 'vi' as LocaleType,
    //   });
    //   initializeApp();
    //   return;
    // }
    const tokenHandler = new BaseTokenRequestHandler(new FetchRequestor());
    const authorizationHandler = new RedirectRequestHandler(new LocalStorageBackend(), new NoHashQueryStringUtils(), window.location, new DefaultCrypto());
    const notifier = new AuthorizationNotifier();
    authorizationHandler.setAuthorizationNotifier(notifier);
    // authorization_endpointのコールバックリスナー(認証に成功していれば、認可コードを取得できる)
    notifier.setAuthorizationListener((request, response, _) => {
      if (response) {
        let extras: StringMap | undefined;
        if (request && request.internal) {
          extras = {
            code_verifier: request.internal.code_verifier,
          };
        }

        const tokenRequest = new TokenRequest({
          client_id: environment.clientId,
          redirect_uri: environment.redirectURL,
          grant_type: GRANT_TYPE_AUTHORIZATION_CODE,
          code: response.code,
          refresh_token: undefined,
          extras,
        });

        // .well-knownエンドポイントからOIDCのエンドポイント情報を取得
        AuthorizationServiceConfiguration.fetchFromIssuer(environment.OPServer, new FetchRequestor())
        // トークンエンドポイントにアクセスし、アクセストークンを取得する
          .then((oResponse) => tokenHandler.performTokenRequest(oResponse, tokenRequest))
          .then((oResponse) => {
            const user = initializeUser(oResponse);
            initializeApp();
            const properties = getUserProperties(user);
            appInsights.trackEvent({ name: 'XgrabLogin' }, properties);
          })
          .catch((oError) => {
            console.error(oError);
          });
      }
    });

    const params = new URLSearchParams(window.location.search);
    const codeParams: string | null = params.get('code');
    if (!codeParams) {
      alert('Unable to get authorization code');
      return;
    }

    authorizationHandler.completeAuthorizationRequestIfPossible();
  }, [setAccessToken, setUser, setIdToken, setRefreshToken, setRefreshInterval, loadDefaultProject, loadProject, updateDefaultProject, setPositionType]);

  return (
    <FpLoading />
  );
}
